import { Injectable, inject } from '@angular/core';
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import {
  MindCatalogItem,
  MindCatalogResponse,
  MindIsAvailableResponse,
} from "../models/mind-catalog.model";
import { environment } from "../../../environments/environment";
import { unformatCnpj } from "../utils/cnpj.util";

import { ApiService } from "./api.service";
import { UtmPreservationService } from "./utm-preservation.service";

@Injectable({ providedIn: 'root' })
export class MindCatalogService {
  private readonly API_URL = `${environment.externalGateServiceUrl}/mind`;
  private apiService = inject(ApiService);
  private utmPreservationService = inject(UtmPreservationService);


  isAvailable(cnpj: string): Observable<MindIsAvailableResponse> {
    const cleanCnpj = unformatCnpj(cnpj);
    const url = `${environment.apiUrl}/mind/isAvailable/${encodeURIComponent(cleanCnpj)}`;
    return this.apiService.getExternal<MindIsAvailableResponse>(url);
  }

  getCatalog(cep: string, number: string, slugOverride?: string | null): Observable<MindCatalogResponse> {
    const slug =
      slugOverride !== undefined && slugOverride !== null
        ? String(slugOverride).trim() || undefined
        : this.resolveSlugFromUtm();
    const slugQuery = slug ? `&slug=${encodeURIComponent(slug)}` : '';
    const url = `${this.API_URL}/catalog?cep=${cep}&number=${number}${slugQuery}`;

    return this.apiService.getExternal<MindCatalogResponse>(url);
  }

  getCatalogProductById(
    productId: number,
    cep: string,
    number: string,
    slugOverride?: string | null
  ): Observable<MindCatalogItem> {
    const slug =
      slugOverride !== undefined && slugOverride !== null
        ? String(slugOverride).trim() || undefined
        : this.resolveSlugFromUtm();
    const slugQuery = slug ? `&slug=${encodeURIComponent(slug)}` : '';
    const cleanCep = (cep || '').replace(/\D/g, '');
    const url = `${this.API_URL}/catalog/${productId}?cep=${encodeURIComponent(cleanCep)}&number=${encodeURIComponent(number || '')}${slugQuery}`;

    return this.apiService
      .getExternal<MindCatalogItem | { data: MindCatalogItem }>(url)
      .pipe(
        map(raw => {
          if (raw && typeof raw === 'object' && 'data' in raw && (raw as { data: MindCatalogItem }).data) {
            return (raw as { data: MindCatalogItem }).data;
          }
          return raw as MindCatalogItem;
        })
      );
  }

  private resolveSlugFromUtm(): string | undefined {
    if (!this.utmPreservationService.isUtmEnabled()) return undefined;
    const source = (this.utmPreservationService.preservedUtmParams?.['utm_source'] ?? '').trim();
    return source || undefined;
  }
}